import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { loadConfig } from './config.js';
import { waitForHandshake, type VarioHandshake } from './vario-handshake.js';
import { exchangeAppToken, AuthBridgeFailure, type AuthBridgeSession } from './auth-bridge-client.js';
import { renderInstall } from './views/install.js';
import { renderDashboard } from './views/dashboard.js';
import { renderUninstall } from './views/uninstall.js';
import { renderListingStatus } from './views/listing-status.js';
import { renderCredentials } from './views/credentials.js';
import { renderLog } from './views/tabs/log.js';
import { renderOrders } from './views/tabs/orders.js';
import { renderOffers } from './views/tabs/offers.js';
import { renderSettings, renderMcpSlot } from './views/tabs/settings.js';
import { renderMasterdataSlot, captureVarioHost } from './views/tabs/masterdata.js';
import { renderArticleAssignSlot } from './views/tabs/article-assign-io.js';
import { renderKauflandList } from './views/tabs/kaufland-list.js';
import { renderShell } from './views/shell.js';
import {
  initVarioIframeHeight,
  requestVarioFullHeightWithFallback,
  LISTING_STATUS_HEIGHT,
  signalVarioReady,
} from './vario-iframe.js';
import { isStandaloneMode, runStandaloneLogin } from './standalone-login.js';

// Bridge-Layer: aliased die kl-*/v-*-Klassen der noch-nicht-migrierten Tabs
// (orders, offers, lister) auf das Brand-Design ohne Markup-Aenderung. MUSS
// als allerletzter CSS-Import laden, damit die Aliase Specificity-Gewinn
// haben gegen die Legacy-Regeln in patterns.css.
import '@vario-connect/frontend-design/bridge.css';

// Eigene Regeln des Kategorie-Modals (views/tabs/settings.ts). NACH bridge.css, weil sie
// dessen `.kl-search-results`/`.kl-search-result` fuer genau dieses Fenster enger fassen.
// Bewusst eine eigene Datei statt einer Aenderung an app.css/bridge.css: die beiden tragen
// jedes andere Modal dieser App und liegen zusaetzlich als Kopie im goe-connect-Repo.
import './styles/kategorie-modal.css';

// Gestaltung des Listing-Detail-Fensters nach dem Entwurf „Kaufland Kategorie.html"
// (2026-08-17). Ebenfalls NACH bridge.css und aus demselben Grund: Die Datei legt sich
// auf bestehende Klassen, statt sie umzubenennen — die Verdrahtung bleibt damit
// unangetastet. Alles darin ist auf `.klst-detail` gescopet.
import './styles/listing-detail-design.css';

const trace = (...args: unknown[]) => console.log('[kaufland-app]', ...args);
const traceErr = (...args: unknown[]) => console.error('[kaufland-app]', ...args);

// `masterdata` war 2026-07-28 entfallen (appVersion 2.17.0) und ist 2026-08-07 mit anderem
// Inhalt zurück (appVersion 2.27.0): der öffentlichen Vario-Domain des Mandanten. Ohne sie
// ist kein Bild-Link für Kaufland abrufbar und kein neues Produkt kommt durch die Prüfung.
type Slot = 'install' | 'uninstall' | 'dashboard' | 'settings' | 'listing-status' | 'log' | 'orders' | 'offers' | 'list' | 'credentials' | 'assign' | 'mcp' | 'masterdata';

function readSlot(): Slot {
  const hash = window.location.hash.replace(/^#\/?/, '').toLowerCase();
  if (
    hash === 'install' || hash === 'uninstall' || hash === 'settings' ||
    hash === 'listing-status' || hash === 'log' ||
    hash === 'orders' || hash === 'offers' || hash === 'list' || hash === 'credentials' ||
    hash === 'assign' || hash === 'mcp' || hash === 'masterdata'
  ) {
    return hash;
  }
  return 'dashboard';
}

async function bootstrap() {
  const standalone = isStandaloneMode();

  // Hoehen-Handling nur im Vario-Slot — postMessage ins Leere ist silent fail, aber
  // sauberer den Helper komplett zu skippen.
  //   - install/uninstall: Varios Installations-MODAL, kein Fenster-Slot. Deshalb bewusst
  //     KEIN useFullHeight (das schiebt Varios eigenen Dialog-Button weit unter die Karte),
  //     sondern Auto-Grow auf die Inhaltshoehe. Die Views setzen dazu ueber
  //     ensureKfDialogShell() den html.kf-dialog-autogrow-Marker, der die bridge-§16-
  //     100%-Kette aufbricht; ohne ihn melden wir nur die Viewport-Hoehe.
  //   - alle anderen Slots: volle verfuegbare Hoehe beim Host anfordern statt die eigene
  //     Content-Hoehe zu melden (Vario-Hinweis 2026-08-01). Das beendet das Render-Springen
  //     und ersetzt die handkalibrierten Festwerte, die es nur gab, weil Vario uns die
  //     verfuegbare Hoehe frueher nicht durchreichte. Das Layout ist dafuer bereits ausgelegt
  //     (bridge.css §16 bzw. .kls-shell), das Scrolling passiert intern.
  //     Die Festwerte bleiben als Watchdog-Rueckfall, falls ein Host useFullHeight ignoriert.
  if (!standalone) {
    const slot = readSlot();
    if (slot === 'install' || slot === 'uninstall') {
      // Min 360: die kompakte Ergebnis-Karte soll nicht auf 540 aufgeblasen werden.
      // Max 1200: Formular + Fehlermeldung darf ueber 900 wachsen, ohne unten abgeschnitten
      // zu werden (Vario clippt den iframe-Container, es gibt keinen inneren Scrollbalken).
      initVarioIframeHeight('auto', undefined, 360, 1200);
    } else {
      requestVarioFullHeightWithFallback(
        slot === 'listing-status' ? LISTING_STATUS_HEIGHT : undefined,
      );
    }
  }

  trace('bootstrap start', {
    href: window.location.href,
    origin: window.location.origin,
    standalone,
    isInIframe: window.parent !== window,
    parentOrigin: (() => {
      try { return window.parent !== window ? document.referrer : '(top)'; } catch { return '(crossorigin)'; }
    })(),
  });

  const statusEl = document.querySelector<HTMLParagraphElement>('#status')!;
  const dotEl = document.querySelector<HTMLSpanElement>('#status-dot')!;
  const contentEl = document.querySelector<HTMLElement>('#content')!;

  const setStatus = (text: string, kind: 'info' | 'ok' | 'error' = 'info') => {
    dotEl.className = `status-dot status-dot--${kind}`;
    dotEl.title = text;
    if (kind === 'error') {
      statusEl.textContent = text;
      statusEl.className = 'error';
      statusEl.hidden = false;
    } else {
      statusEl.hidden = true;
      statusEl.textContent = '';
    }
    trace('status:', text);
  };

  let config;
  try {
    config = loadConfig();
  } catch (e) {
    setStatus(`Konfiguration unvollständig: ${(e as Error).message}`, 'error');
    return;
  }

  // Auth-Branch: Standalone-Login (Design-Vorschau) vs. Vario-Slot-Handshake.
  // Beide Pfade liefern dieselbe AuthBridgeSession-Shape und denselben
  // VarioHandshake-Shape (fake-Token im Standalone-Pfad), damit der Rest
  // unverändert funktioniert.
  let session: AuthBridgeSession;
  let handshake: VarioHandshake;

  if (standalone) {
    setStatus('Standalone-Login …', 'info');
    try {
      session = await runStandaloneLogin({
        contentEl,
        supabaseUrl: config.supabaseUrl,
        supabaseAnonKey: config.supabaseAnonKey,
      });
      // Fake-Handshake — wird vom dashboard-Slot durchgereicht, aber nicht
      // aktiv genutzt (nur listing-status/-category greifen darauf zu, die
      // im Standalone-Mode nicht angesprungen werden).
      handshake = { appToken: session.token, appIdentifier: 'standalone-bypass' };
    } catch (e) {
      setStatus(`Standalone-Login fehlgeschlagen: ${(e as Error).message}`, 'error');
      return;
    }
  } else {
    setStatus('Warte auf Vario-Handshake …');
    try {
      handshake = await waitForHandshake(config.allowedHostOrigins);
    } catch (e) {
      setStatus(`Handshake fehlgeschlagen: ${(e as Error).message}`, 'error');
      return;
    }

    setStatus('Tausche Token gegen Supabase-Session …');
    try {
      session = await exchangeAppToken(config.authBridgeUrl, handshake.appToken);
    } catch (e) {
      if (e instanceof AuthBridgeFailure) {
        // Abgelaufener/ungültiger Vario-App-Token (z. B. Fenster lag lange offen, bevor es
        // geöffnet/aktiviert wurde): handlungsleitende statt kryptischer Meldung. Ein iframe-
        // Reload würde denselben (abgelaufenen) `?appToken` aus der URL erneut tauschen →
        // bewusst KEIN Auto-Reload (Endlosschleife). Der frische Token kommt nur, wenn Vario
        // das Fenster neu öffnet. Server-seitiger Clock-Skew ist via clockTolerance abgefangen.
        if (e.code === 'TOKEN_VERIFY_FAILED') {
          setStatus(
            'Sitzung abgelaufen — bitte das Kaufland-Fenster in Vario schließen und neu öffnen (der Sicherheits-Token war nicht mehr gültig).',
            'error',
          );
        } else {
          setStatus(`Auth-Bridge ${e.status}: ${e.code} — ${e.message}`, 'error');
        }
      } else {
        setStatus(`Auth-Bridge unerreichbar: ${(e as Error).message}`, 'error');
      }
      return;
    }
  }

  const supabase: SupabaseClient = createClient(config.supabaseUrl, config.supabaseAnonKey, {
    global: { headers: { Authorization: `Bearer ${session.token}` } },
    auth: { persistSession: false, autoRefreshToken: false },
  });

  setStatus(`Verbunden — Tenant ${session.tenantId.slice(0, 8)}…`, 'ok');

  // Die öffentliche Vario-Domain des Mandanten bei JEDEM Slot-Aufruf mitnehmen, nicht nur im
  // Stammdaten-Tab. Sie steht in `document.referrer`, also in jedem Aufruf ohnehin drin, und
  // ein Händler, der den Tab nie öffnet, hätte sonst dauerhaft keine, mit der Folge, dass
  // Kaufland seine Bilder nicht laden kann und kein Produkt freigibt.
  //
  // Bewusst ohne `await`: Das ist ein Nebenzweck. Kein Slot soll darauf warten, und ein
  // Fehler darf keinen Render aufhalten (die Funktion schluckt ihre Fehler selbst).
  void captureVarioHost(supabase);

  const slot = readSlot();
  trace('routing to slot', slot);

  const ctx = {
    contentEl,
    supabase,
    session: {
      token: session.token,
      tenantId: session.tenantId,
      marketplaceKind: session.marketplaceKind,
    },
    handshake,
    supabaseUrl: config.supabaseUrl,
  };

  // Standalone (außerhalb Vario): App-Shell mit Sidebar als Marktplatz-Umschalter.
  // Der Vario-Slot-Pfad (switch unten) bleibt unberührt.
  if (standalone) {
    await renderShell(ctx);
    return;
  }

  switch (slot) {
    case 'install':
      await renderInstall(ctx);
      break;
    case 'dashboard':
      await renderDashboard(ctx);
      break;
    case 'uninstall':
      await renderUninstall(ctx);
      break;
    case 'listing-status':
      await renderListingStatus({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'log':
      renderLog({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'orders':
      renderOrders({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'offers':
      renderOffers({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'list':
      renderKauflandList({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'settings':
      renderSettings({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'masterdata':
      renderMasterdataSlot({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'mcp':
      renderMcpSlot({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'assign':
      await renderArticleAssignSlot({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
    case 'credentials':
      await renderCredentials({
        contentEl,
        supabase,
        session: { token: session.token, tenantId: session.tenantId, marketplaceKind: session.marketplaceKind },
        supabaseUrl: config.supabaseUrl,
      });
      break;
  }

  // VARIO-Ready-Signal: App vollständig geladen und im Vario-iframe erreichbar
  // (Host wertet ab 2026-08-20 aus). Nur im Erfolgsfall; Fehler-/Standalone-Pfade
  // erreichen diese Stelle nicht.
  signalVarioReady();
}

bootstrap().catch((err) => {
  traceErr('bootstrap fatal', err);
  const statusEl = document.querySelector<HTMLParagraphElement>('#status');
  if (statusEl) {
    statusEl.textContent = `Bootstrap-Fehler: ${(err as Error).message ?? err}`;
    statusEl.className = 'error';
  }
});

window.addEventListener('error', (e) => traceErr('window error', e.error ?? e.message));
window.addEventListener('unhandledrejection', (e) => traceErr('unhandled rejection', e.reason));
